import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import {
  CatalogOffering,
  CatalogService,
  CatalogWarehouse,
  InventoryCountLineInput,
  InventoryCountSession
} from './catalog.service';

interface OptionItem {
  label: string;
  value: string;
}

type CountStageTab = 'plan' | 'counting' | 'review' | 'close';

@Component({
  standalone: true,
  selector: 'ain-inventory-count-sessions',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InventoryPageHintComponent,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TabsModule
  ],
  template: `
    <ain-page [title]="t('catalog.counts.title')" [description]="t('catalog.counts.description')">
      <ain-inventory-page-hint pageKey="countSessions" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="form-card">
          <h3>{{ t('catalog.counts.createTitle') }}</h3>
          <div class="form-grid">
            <label>
              <span>{{ t('catalog.counts.warehouse') }}</span>
              <p-select [(ngModel)]="createForm.warehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.counts.offering') }}</span>
              <p-select [(ngModel)]="createForm.offeringId" [options]="offeringOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.quantity') }}</span>
              <input pInputText type="number" min="0" step="0.001" [(ngModel)]="createForm.countedQuantity" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.reference') }}</span>
              <input pInputText [(ngModel)]="createForm.referenceNumber" />
            </label>
          </div>
          <div class="actions">
            <button pButton type="button" [label]="t('catalog.action.create')" (click)="createSession()"></button>
          </div>
        </section>

        <section class="list-card">
          <h3>{{ t('catalog.counts.historyTitle') }}</h3>
          <p-table [value]="sessions()" [paginator]="true" [rows]="15" selectionMode="single" [(selection)]="selectedSessionRow" dataKey="id" (onRowSelect)="onSessionSelected()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.inventory.reference') }}</th>
                <th>{{ t('catalog.counts.warehouse') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('catalog.counts.variance') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr [pSelectableRow]="row">
                <td>{{ row.referenceNumber }}</td>
                <td>{{ resolveWarehouseLabel(row.warehouseId) }}</td>
                <td><span class="status-badge" [class]="statusClass(row.status)">{{ statusLabel(row.status) }}</span></td>
                <td>{{ totalVariance(row) }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        @if (selectedSession()) {
          <section class="detail-card">
            <header class="detail-header">
              <div>
                <h3>{{ selectedSession()!.referenceNumber }}</h3>
                <p>{{ t('catalog.counts.selectedSessionHint') }}</p>
              </div>
              <span class="status-badge" [class]="statusClass(selectedSession()!.status)">{{ statusLabel(selectedSession()!.status) }}</span>
            </header>

            <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
              <p-tablist>
                <p-tab value="plan">{{ t('catalog.counts.tab.plan') }}</p-tab>
                <p-tab value="counting">{{ t('catalog.counts.tab.counting') }}</p-tab>
                <p-tab value="review">{{ t('catalog.counts.tab.review') }}</p-tab>
                <p-tab value="close">{{ t('catalog.counts.tab.close') }}</p-tab>
              </p-tablist>
              <p-tabpanels>
                <p-tabpanel value="plan">
                  <p>{{ t('catalog.counts.planHint') }}</p>
                  @if (canStartCounting()) {
                    <button pButton type="button" [label]="t('catalog.counts.startCounting')" (click)="startCounting()"></button>
                  }
                  @if (canCancel()) {
                    <button pButton type="button" severity="secondary" class="ml-8" [label]="t('catalog.counts.cancel')" (click)="cancelSession()"></button>
                  }
                </p-tabpanel>

                <p-tabpanel value="counting">
                  <p>{{ t('catalog.counts.countingHint') }}</p>
                  <div class="form-grid">
                    <label>
                      <span>{{ t('catalog.counts.offering') }}</span>
                      <p-select [(ngModel)]="roundForm.offeringId" [options]="offeringOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
                    </label>
                    <label>
                      <span>{{ t('catalog.inventory.quantity') }}</span>
                      <input pInputText type="number" min="0" step="0.001" [(ngModel)]="roundForm.countedQuantity" [disabled]="!canUpdateRound()" />
                    </label>
                    <label>
                      <span>{{ t('catalog.counts.roundNumber') }}</span>
                      <input pInputText type="number" min="1" step="1" [(ngModel)]="roundForm.roundNumber" [disabled]="!canUpdateRound()" />
                    </label>
                    <label class="checkbox-row">
                      <p-checkbox [(ngModel)]="roundForm.isBlindCount" [binary]="true" [disabled]="!canUpdateRound()" />
                      <span>{{ t('catalog.counts.blindCount') }}</span>
                    </label>
                    <label class="wide">
                      <span>{{ t('catalog.counts.discrepancyReason') }}</span>
                      <input pInputText [(ngModel)]="roundForm.discrepancyReason" [disabled]="!canUpdateRound()" />
                    </label>
                  </div>
                  <div class="actions">
                    @if (canUpdateRound()) {
                      <button pButton type="button" [label]="t('catalog.counts.updateRound')" (click)="updateRound(false)"></button>
                      <button pButton type="button" severity="secondary" [label]="t('catalog.counts.moveToReview')" (click)="updateRound(true)"></button>
                    }
                  </div>
                </p-tabpanel>

                <p-tabpanel value="review">
                  <p>{{ t('catalog.counts.reviewHint') }}</p>
                  <p-table [value]="selectedSession()!.lines">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ t('catalog.counts.offering') }}</th>
                        <th>{{ t('catalog.counts.expected') }}</th>
                        <th>{{ t('catalog.inventory.quantity') }}</th>
                        <th>{{ t('catalog.counts.variance') }}</th>
                        <th>{{ t('catalog.counts.roundNumber') }}</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-line>
                      <tr>
                        <td>{{ resolveOfferingLabel(line.offeringId) }}</td>
                        <td>{{ line.expectedQuantity }}</td>
                        <td>{{ line.countedQuantity }}</td>
                        <td>{{ line.varianceQuantity }}</td>
                        <td>{{ line.roundNumber }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                  @if (canMoveToReview()) {
                    <div class="actions">
                      <button pButton type="button" [label]="t('catalog.counts.moveToReview')" (click)="moveToReview()"></button>
                    </div>
                  }
                </p-tabpanel>

                <p-tabpanel value="close">
                  <p>{{ t('catalog.counts.closeHint') }}</p>
                  <label class="wide">
                    <span>{{ t('catalog.counts.closeReason') }}</span>
                    <input pInputText [(ngModel)]="closeForm.reason" [disabled]="!canClose()" />
                  </label>
                  @if (canClose()) {
                    <div class="actions">
                      <button pButton type="button" severity="danger" [label]="t('catalog.counts.close')" (click)="closeSession()"></button>
                    </div>
                  }
                </p-tabpanel>
              </p-tabpanels>
            </p-tabs>
          </section>
        } @else {
          <p class="muted">{{ t('catalog.counts.selectSession') }}</p>
        }

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .form-card, .list-card, .detail-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-bottom: 12px; background: #fff; }
    .form-card h3, .list-card h3, .detail-card h3 { margin: 0 0 10px; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid label.wide { grid-column: 1 / -1; }
    .form-grid label.checkbox-row { display: flex; align-items: center; gap: 8px; margin-top: 24px; }
    .form-grid span { color: #475569; font-size: 12px; font-weight: 600; }
    .actions { margin-top: 10px; display: flex; gap: 8px; flex-wrap: wrap; }
    .detail-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 12px; }
    .detail-header p { margin: 4px 0 0; color: #64748b; font-size: 13px; }
    .status-badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 12px; font-weight: 600; background: #e2e8f0; color: #334155; }
    .status-badge.is-plan { background: #dbeafe; color: #1d4ed8; }
    .status-badge.is-counting { background: #fef3c7; color: #b45309; }
    .status-badge.is-review { background: #ede9fe; color: #6d28d9; }
    .status-badge.is-closed { background: #dcfce7; color: #15803d; }
    .status-badge.is-cancelled { background: #fee2e2; color: #b91c1c; }
    .error { margin: 10px 0 0; color: #b91c1c; }
    .muted { color: #64748b; margin: 0 0 12px; }
    .ml-8 { margin-inline-start: 8px; }
  `]
})
export class InventoryCountSessionsComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly sessions = signal<InventoryCountSession[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly selectedSessionId = signal<string | null>(null);
  selectedSessionRow: InventoryCountSession | null = null;
  readonly activeTab = signal<CountStageTab>('plan');

  readonly createForm = {
    warehouseId: '',
    offeringId: '',
    countedQuantity: 0,
    referenceNumber: ''
  };

  readonly roundForm = {
    offeringId: '',
    countedQuantity: 0,
    roundNumber: 1,
    isBlindCount: false,
    discrepancyReason: ''
  };

  readonly closeForm = {
    reason: ''
  };

  readonly selectedSession = computed(() => {
    const id = this.selectedSessionId();
    if (!id) return this.selectedSessionRow;
    return this.sessions().find(item => item.id === id) ?? this.selectedSessionRow;
  });

  readonly canStartCounting = computed(() => this.selectedSession()?.status === 'Plan');
  readonly canUpdateRound = computed(() => this.selectedSession()?.status === 'Counting');
  readonly canMoveToReview = computed(() => this.selectedSession()?.status === 'Counting');
  readonly canClose = computed(() => {
    const status = this.selectedSession()?.status;
    return status === 'Review' || status === 'Counting';
  });
  readonly canCancel = computed(() => {
    const status = this.selectedSession()?.status;
    return status === 'Plan' || status === 'Counting' || status === 'Review';
  });

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.error.set('');

    this.service.listOfferings().subscribe({
      next: offerings => {
        this.offerings.set(offerings.filter(item => item.inventoryEnabled));
        if (!this.createForm.offeringId && this.offerings().length > 0)
          this.createForm.offeringId = this.offerings()[0].id;

        this.service.listWarehouses().subscribe({
          next: warehouses => {
            this.warehouses.set(warehouses.filter(item => item.isActive));
            if (!this.createForm.warehouseId && this.warehouses().length > 0)
              this.createForm.warehouseId = this.warehouses()[0].id;

            this.service.listInventoryCountSessions().subscribe({
              next: sessions => {
                this.sessions.set(sessions);
                const selectedId = this.selectedSessionId() ?? this.selectedSessionRow?.id;
                if (selectedId) {
                  this.selectedSessionId.set(selectedId);
                  const refreshed = sessions.find(item => item.id === selectedId) ?? null;
                  this.selectedSessionRow = refreshed;
                  if (refreshed) this.syncRoundForm(refreshed);
                }
                this.loading.set(false);
              },
              error: () => {
                this.error.set(this.t('catalog.counts.loadFailed'));
                this.loading.set(false);
              }
            });
          },
          error: () => {
            this.error.set(this.t('catalog.counts.loadFailed'));
            this.loading.set(false);
          }
        });
      },
      error: () => {
        this.error.set(this.t('catalog.counts.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  createSession(): void {
    this.error.set('');
    if (!this.createForm.warehouseId || !this.createForm.offeringId || this.createForm.countedQuantity < 0) {
      this.error.set(this.t('catalog.counts.requiredFields'));
      return;
    }

    this.service.createInventoryCountSession({
      warehouseId: this.createForm.warehouseId,
      referenceNumber: this.createForm.referenceNumber.trim(),
      startInCounting: false,
      lines: [{
        offeringId: this.createForm.offeringId,
        binId: null,
        countedQuantity: Number(this.createForm.countedQuantity),
        roundNumber: 1,
        isBlindCount: false
      }]
    }).subscribe({
      next: session => {
        this.selectedSessionRow = session;
        this.selectedSessionId.set(session.id);
        this.activeTab.set('plan');
        this.syncRoundForm(session);
        this.load(false);
      },
      error: () => this.error.set(this.t('catalog.counts.createFailed'))
    });
  }

  startCounting(): void {
    const session = this.selectedSession();
    if (!session) return;
    this.service.startInventoryCountSession(session.id).subscribe({
      next: () => {
        this.activeTab.set('counting');
        this.load(false);
      },
      error: () => this.error.set(this.t('catalog.counts.startFailed'))
    });
  }

  updateRound(moveToReview: boolean): void {
    const session = this.selectedSession();
    if (!session || !this.roundForm.offeringId) return;

    const payload = {
      lines: [this.buildRoundLine()],
      moveToReview
    };

    this.service.updateInventoryCountRound(session.id, payload).subscribe({
      next: updated => {
        this.selectedSessionRow = updated;
        this.selectedSessionId.set(updated.id);
        this.syncRoundForm(updated);
        if (moveToReview) this.activeTab.set('review');
        this.load(false);
      },
      error: () => this.error.set(this.t('catalog.counts.roundFailed'))
    });
  }

  moveToReview(): void {
    const session = this.selectedSession();
    if (!session) return;
    this.service.moveInventoryCountSessionToReview(session.id).subscribe({
      next: () => {
        this.activeTab.set('review');
        this.load(false);
      },
      error: () => this.error.set(this.t('catalog.counts.reviewFailed'))
    });
  }

  closeSession(): void {
    const session = this.selectedSession();
    if (!session) return;
    const reason = this.closeForm.reason.trim();
    if (!reason) {
      this.error.set(this.t('catalog.counts.closeReasonRequired'));
      return;
    }

    this.service.closeInventoryCountSession(session.id, {
      idempotencyKey: crypto.randomUUID(),
      reason
    }).subscribe({
      next: () => {
        this.activeTab.set('close');
        this.load(false);
      },
      error: () => this.error.set(this.t('catalog.counts.closeFailed'))
    });
  }

  cancelSession(): void {
    const session = this.selectedSession();
    if (!session) return;
    const reason = this.t('catalog.counts.cancelDefaultReason');
    this.service.cancelInventoryCountSession(session.id, reason).subscribe({
      next: () => this.load(false),
      error: () => this.error.set(this.t('catalog.counts.cancelFailed'))
    });
  }

  onSessionSelected(): void {
    const session = this.selectedSessionRow;
    if (!session) return;
    this.selectedSessionId.set(session.id);
    this.syncRoundForm(session);
    this.activeTab.set(this.tabForStatus(session.status));
    this.closeForm.reason = '';
    this.error.set('');
  }

  onTabChange(value: string | number | undefined): void {
    if (value === 'plan' || value === 'counting' || value === 'review' || value === 'close')
      this.activeTab.set(value);
  }

  offeringOptions(): OptionItem[] {
    return this.offerings().map(item => ({ label: `${item.code} - ${item.title}`, value: item.id }));
  }

  warehouseOptions(): OptionItem[] {
    return this.warehouses().map(item => ({ label: `${item.code} - ${item.name}`, value: item.id }));
  }

  resolveOfferingLabel(offeringId: string | null | undefined): string {
    if (!offeringId) return '-';
    const offering = this.offerings().find(item => item.id === offeringId);
    return offering ? `${offering.code} - ${offering.title}` : '-';
  }

  resolveWarehouseLabel(warehouseId: string | null | undefined): string {
    if (!warehouseId) return '-';
    const warehouse = this.warehouses().find(item => item.id === warehouseId);
    return warehouse ? `${warehouse.code} - ${warehouse.name}` : '-';
  }

  statusLabel(status: string): string {
    const key = `catalog.counts.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  statusClass(status: string): string {
    switch (status) {
      case 'Plan': return 'is-plan';
      case 'Counting': return 'is-counting';
      case 'Review': return 'is-review';
      case 'Closed': return 'is-closed';
      case 'Cancelled': return 'is-cancelled';
      default: return '';
    }
  }

  totalVariance(session: InventoryCountSession): number {
    return (session.lines ?? []).reduce((sum, line) => sum + (line.varianceQuantity ?? 0), 0);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private tabForStatus(status: string): CountStageTab {
    switch (status) {
      case 'Plan': return 'plan';
      case 'Counting': return 'counting';
      case 'Review': return 'review';
      default: return 'close';
    }
  }

  private syncRoundForm(session: InventoryCountSession): void {
    const line = session.lines?.[0];
    this.roundForm.offeringId = line?.offeringId ?? this.createForm.offeringId;
    this.roundForm.countedQuantity = line?.countedQuantity ?? 0;
    this.roundForm.roundNumber = line?.roundNumber ?? 1;
    this.roundForm.isBlindCount = line?.isBlindCount ?? false;
    this.roundForm.discrepancyReason = line?.discrepancyReason ?? '';
  }

  private buildRoundLine(): InventoryCountLineInput {
    return {
      offeringId: this.roundForm.offeringId,
      binId: null,
      countedQuantity: Number(this.roundForm.countedQuantity),
      roundNumber: Number(this.roundForm.roundNumber),
      isBlindCount: this.roundForm.isBlindCount,
      discrepancyReason: this.roundForm.discrepancyReason.trim() || null
    };
  }
}
